import { useId } from 'react';
import { cn } from '@/lib/utils';

export interface ProjectChoice {
  mode: 'new' | 'existing';
  name: string;
  projectId: string;
}

interface ProjectChoiceFieldsProps {
  value: ProjectChoice;
  onChange: (value: ProjectChoice) => void;
  projects: { id: string; title: string }[];
}

export function ProjectChoiceFields({ value, onChange, projects }: ProjectChoiceFieldsProps) {
  const group = useId();
  const set = (patch: Partial<ProjectChoice>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-3">
      <label
        className={cn(
          'flex flex-col gap-2 rounded-xl border p-3 transition cursor-pointer',
          value.mode === 'new'
            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
            : 'border-border hover:border-border/80 hover:bg-accent/40',
        )}
      >
        <div className="flex items-center gap-2">
          <input
            type="radio"
            name={group}
            checked={value.mode === 'new'}
            onChange={() => set({ mode: 'new' })}
            className="accent-primary h-4 w-4"
          />
          <span className="text-sm font-medium">New project</span>
          <span className="ml-auto text-[10px] text-muted-foreground">Start fresh</span>
        </div>
        {value.mode === 'new' && (
          <input
            type="text"
            value={value.name}
            onChange={e => set({ name: e.target.value })}
            placeholder="Project name"
            autoFocus
            className="ml-6 h-9 rounded-lg border border-border bg-card px-3 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
          />
        )}
      </label>

      <label
        className={cn(
          'flex flex-col gap-2 rounded-xl border p-3 transition',
          !projects.length && 'opacity-50 cursor-not-allowed',
          projects.length && value.mode === 'existing' && 'border-primary bg-primary/5 ring-2 ring-primary/20 cursor-pointer',
          projects.length && value.mode !== 'existing' && 'border-border hover:border-border/80 hover:bg-accent/40 cursor-pointer',
        )}
      >
        <div className="flex items-center gap-2">
          <input
            type="radio"
            name={group}
            checked={value.mode === 'existing'}
            onChange={() => projects.length && set({ mode: 'existing' })}
            disabled={!projects.length}
            className="accent-primary h-4 w-4"
          />
          <span className="text-sm font-medium">Existing project</span>
          <span className="ml-auto text-[10px] text-muted-foreground">
            {projects.length ? `${projects.length} available` : 'None yet'}
          </span>
        </div>
        {value.mode === 'existing' && projects.length > 0 && (
          <select
            value={value.projectId}
            onChange={e => set({ projectId: e.target.value })}
            className="ml-6 h-9 rounded-lg border border-border bg-card px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
          >
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        )}
      </label>
    </div>
  );
}
